import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { errorMessage } from '@/api/common';
import { organizationApi } from '@/api/account-management/organizationApi';
import { mediaNetworkApi } from '@/api/media-network/mediaNetworkApi';
import { mediaNetworkKeys } from '@/api/media-network/mediaNetworkQueries';
import type { Advertiser } from '@/models/media-network/Advertiser';
import type { Organization } from '@/models/account-management/Organization';
import { useLocalizedStrings } from '../../../localization/LocalizedStringsProvider';
import { useToast } from '../../common/toast/ToastProvider';

interface Props {
    open: boolean;
    advertiser: Advertiser;
    onClose: () => void;
}

// Which clients hold this advertiser — ADVERTISER → BRANDS, because that is the direction the agreement runs:
// one deal with Pepsi, then the brands it runs at.
//
// A grant is what makes the advertiser visible to a brand at all. Until one exists, that brand cannot see the
// advertiser, cannot pick it, and does not know it is on the network.
const AdvertiserGrantsDialog = ({ open, advertiser, onClose }: Props) => {
    const strings = useLocalizedStrings();
    const toast = useToast();
    const qc = useQueryClient();

    const [addingBrandId, setAddingBrandId] = useState('');

    const { data: grants = [], isPending } = useQuery({
        queryKey: mediaNetworkKeys.advertiserGrants(advertiser.id),
        queryFn: () => mediaNetworkApi.listAdvertiserGrants(advertiser.id),
    });

    // Every brand on the platform, which means organisations first. N+1, and acceptable here: this is a
    // super-admin screen opened occasionally, and the alternative is a new cross-org brand endpoint for one
    // picker. If the org count ever makes this slow, that endpoint is the fix.
    const { data: organizations = [] } = useQuery<Organization[]>({
        queryKey: ['organizations'],
        queryFn: () => organizationApi.list(),
        staleTime: Infinity,
    });

    const brandQueries = useQueries({
        queries: organizations.map((o) => ({
            queryKey: ['org-brands', o.id],
            queryFn: () => organizationApi.listBrands(o.id!),
            staleTime: Infinity,
        })),
    });

    const allBrands = useMemo(
        () => organizations.flatMap((o, i) =>
            (brandQueries[i]?.data ?? []).map((b) => ({ id: b.id!, label: `${o.name} — ${b.name}` }))),
        [organizations, brandQueries]);

    const granted = new Set(grants.map((g) => g.brandId));
    const available = allBrands.filter((b) => !granted.has(b.id));
    const brandLabel = (brandId: string) => allBrands.find((b) => b.id === brandId)?.label ?? brandId;

    const refresh = async () => {
        await qc.invalidateQueries({ queryKey: mediaNetworkKeys.advertiserGrants(advertiser.id) });
        // A grant changes what that brand's own advertiser list returns.
        await qc.invalidateQueries({ queryKey: ['playlist-advertisers'] });
    };

    const { mutateAsync: grant, isPending: isGranting } = useMutation({
        mutationFn: (brandId: string) => mediaNetworkApi.grantAdvertiser(advertiser.id, { brandId }),
        onSuccess: async () => {
            setAddingBrandId('');
            await refresh();
        },
        onError: (e: unknown) => toast({ severity: 'error', message: errorMessage(e, strings.advertiserGrantError) }),
    });

    const { mutateAsync: revoke } = useMutation({
        mutationFn: (brandId: string) => mediaNetworkApi.revokeAdvertiser(advertiser.id, brandId),
        onSuccess: refresh,
        // Refused while that brand's creatives still attribute to them, and the message says so — revoking
        // would leave attribution pointing at something the brand can no longer see or reassign.
        onError: (e: unknown) => toast({ severity: 'error', message: errorMessage(e, strings.advertiserRevokeError) }),
    });

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{strings.advertiserBrandsTitle.replace('{{name}}', advertiser.name)}</DialogTitle>

            <DialogContent dividers>
                <Stack spacing={2}>
                    <Alert severity="info" variant="outlined">{strings.advertiserGrantsHelp}</Alert>

                    {!advertiser.enabled && (
                        // Worth saying here: the grants below all look fine and none of them are running.
                        <Alert severity="warning" variant="outlined">{strings.advertiserDisabledEverywhere}</Alert>
                    )}

                    <Stack direction="row" spacing={1} alignItems="center">
                        <FormControl size="small" sx={{ flex: 1 }}>
                            <InputLabel id="grant-brand-label">{strings.brand}</InputLabel>
                            <Select
                                labelId="grant-brand-label"
                                label={strings.brand}
                                value={addingBrandId}
                                onChange={(e) => setAddingBrandId(e.target.value)}
                                disabled={isGranting || available.length === 0}
                            >
                                {available.map((b) => (
                                    <MenuItem key={b.id} value={b.id}>{b.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Button
                            variant="contained"
                            size="input"
                            startIcon={<AddIcon />}
                            loading={isGranting}
                            disabled={!addingBrandId || isGranting}
                            onClick={() => grant(addingBrandId)}
                        >
                            {strings.advertiserGrant}
                        </Button>
                    </Stack>

                    {!isPending && grants.length === 0 && (
                        <Typography variant="body2" color="text.secondary">{strings.advertiserNoGrants}</Typography>
                    )}

                    {grants.map((g) => (
                        <Paper key={g.brandId} variant="outlined" sx={{ p: 1.5 }}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Stack sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" noWrap>{brandLabel(g.brandId)}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {strings.advertiserGrantCreatives.replace('{{count}}', String(g.mediaCount))}
                                        {g.externalId ? ` · ${g.externalId}` : ''}
                                    </Typography>
                                </Stack>

                                {/* The BRAND's veto, shown but not editable here: TouchTab grants availability,
                                    the brand decides whether to run them. Merging the two switches would hide
                                    which party turned it off. */}
                                {!g.enabled && (
                                    <Chip size="small" variant="outlined" color="warning" label={strings.advertiserDeclinedByBrand} />
                                )}

                                <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => revoke(g.brandId)}
                                    aria-label="revoke advertiser"
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Stack>
                        </Paper>
                    ))}
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button variant="outlined" onClick={onClose}>{strings.close}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default AdvertiserGrantsDialog;
